import { z } from "zod";
import {
  orderAttachmentKindEnum,
  orderStatusEnum,
} from "@/server/platform/db/schema/order";

export const ORDER_STATUSES = orderStatusEnum;

export type OrderStatus = (typeof orderStatusEnum)[number];

export const OrderIdParamSchema = z.object({
  id: z.string().min(1),
});

export const OrderListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).default(20),
  offset: z.coerce.number().int().min(0).default(0),
  sort: z
    .array(z.object({ field: z.string(), dir: z.enum(["asc", "desc"]) }))
    .optional(),
  filters: z.array(z.any()).optional(),
});

export const OrderUpdateStatusSchema = z.object({
  status: z.enum(orderStatusEnum),
  note: z.string().optional(),
});

export const OrderUpdateTrackingSchema = z.object({
  trackingNumber: z.string().min(1, "ກະລຸນາກອກເລກຕິດຕາມ"),
});

/** ສ່ວນຫຼຸດທ້າຍບິນ — ອະນຸຍາດສະເພາະສະຖານະ «ລໍຖ້າຊຳລະ» */
export const OrderUpdateDiscountSchema = z.object({
  discountTotal: z.coerce.number().int().min(0),
});

/** ແກ້ລາຍການສິນຄ້າ — pending_payment / confirmed / packing (ບໍ່ມີ return lines) */
export const OrderUpdateItemsSchema = z.object({
  items: z
    .array(
      z.object({
        variantId: z.string().min(1),
        quantity: z.coerce.number().int().min(1),
      }),
    )
    .min(1, "ຕ້ອງມີລາຍການສິນຄ້າຢ່າງໜ້ອຍ 1 ບັນທັດ"),
  note: z.string().trim().min(1, "ຕ້ອງລະບຸເຫດຜົນ").max(500),
});

export type OrderUpdateItemsInput = z.infer<typeof OrderUpdateItemsSchema>;

/** ປ່ຽນວິທີຊຳລະ COD / ອອນໄລນ໌ — ສະເພາະ«ລໍຖ້າຊຳລະ» ບໍ່ມີ payment paid ຫຼື partially_refunded */
export const OrderUpdatePaymentMethodSchema = z.object({
  paymentMethod: z.enum(["cod", "online"]),
});

/** web / retail / social — ສະເພາະ«ລໍຖ້າຊຳລະ». social ຕ້ອງມີ socialSalesSourceId (ແຫຼ່ງເປີດໃຊ້) */
export const OrderUpdateChannelSchema = z
  .object({
    channel: z.enum(["web", "retail", "social"]),
    socialSalesSourceId: z.string().min(1).nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.channel === "social") {
      const sid = data.socialSalesSourceId?.trim();
      if (!sid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ຕ້ອງເລືອກແຫຼ່ງຂາຍເມື່ອຊ່ອງທາງເປັນ Social Media",
          path: ["socialSalesSourceId"],
        });
      }
    }
  });

export type OrderUpdateDiscountInput = z.infer<
  typeof OrderUpdateDiscountSchema
>;
export type OrderUpdatePaymentMethodInput = z.infer<
  typeof OrderUpdatePaymentMethodSchema
>;
export type OrderUpdateChannelInput = z.infer<typeof OrderUpdateChannelSchema>;

/** ແກ້ໄຂຂໍ້ມູນຕິດຕໍ່/ທີ່ຢູ່ຈັດສົ່ງ — ອະນຸຍາດສະເພາະກ່ອນສົ່ງສິນຄ້າ */
export const OrderUpdateCustomerInfoSchema = z
  .object({
    customerName: z.string().min(1).max(200).optional(),
    customerEmail: z.string().email().optional(),
    customerPhone: z.string().max(50).nullable().optional(),
    shippingRecipientName: z.string().min(1).max(200).nullable().optional(),
    shippingPhone: z.string().max(50).nullable().optional(),
    shippingVillage: z.string().min(1).max(1000).nullable().optional(),
    shippingProvinceId: z.string().min(1).nullable().optional(),
    shippingDistrictId: z.string().min(1).nullable().optional(),
    shippingZone: z.string().max(32).nullable().optional(),
    shippingCarrierId: z.string().min(1).nullable().optional(),
    carrierBranch: z.string().max(500).nullable().optional(),
  })
  .refine((o) => Object.keys(o).length > 0, {
    message: "ບໍ່ມີຟິວທີ່ຈະອັບເດດ",
  })
  .superRefine((val, ctx) => {
    if (
      val.shippingCarrierId !== undefined &&
      val.shippingCarrierId !== null &&
      val.shippingCarrierId.length > 0
    ) {
      const b = val.carrierBranch;
      if (
        b === undefined ||
        b === null ||
        (typeof b === "string" && b.trim().length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ກະລຸນາປ້ອນສາຂາເມື່ອເລືອກຜູ້ຂົນສົ່ງ",
          path: ["carrierBranch"],
        });
      }
    }
  });

export type OrderUpdateCustomerInfoInput = z.infer<
  typeof OrderUpdateCustomerInfoSchema
>;

export const OrderReturnLineInputSchema = z.object({
  orderItemId: z.string().min(1),
  quantityReturned: z.coerce.number().int().min(1),
  reason: z.string().max(2000).optional().nullable(),
});

export const OrderCreateReturnLinesSchema = z.object({
  lines: z.array(OrderReturnLineInputSchema).min(1),
});

export const OrderAttachmentKindSchema = z.enum(orderAttachmentKindEnum);

export const OrderCreateAttachmentSchema = z.object({
  kind: OrderAttachmentKindSchema,
  fileKey: z.string().min(1),
  sortOrder: z.coerce.number().int().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const OrderAttachmentIdParamSchema = z.object({
  id: z.string().min(1),
  attachmentId: z.string().min(1),
});

export type OrderUpdateStatusInput = z.infer<typeof OrderUpdateStatusSchema>;
export type OrderUpdateTrackingInput = z.infer<
  typeof OrderUpdateTrackingSchema
>;

export type OrderCreateAttachmentInput = z.infer<
  typeof OrderCreateAttachmentSchema
>;

export type OrderAttachmentKind = z.infer<typeof OrderAttachmentKindSchema>;

export interface OrderAttachmentDTO {
  id: string;
  orderId: string;
  kind: OrderAttachmentKind;
  fileKey: string;
  sortOrder: number;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  createdBy: string | null;
}

export interface OrderItemDTO {
  id: string;
  orderId: string;
  variantId: string;
  quantity: number;
  price: number;
  total: number;
  /** ชื่อสินค้า/variant จาก catalog */
  productName?: string;
  variantSku?: string;
  /** Variant weight in grams; null means use shipping default. */
  weightGrams?: number | null;
  /** ຊື່ຕົວເລືອກສິນຄ້າ + ຄ່າ (i18n) — ຈາກ product_option / product_option_value */
  resolvedVariantOptions?: Array<{
    templateName: string;
    valueName: string;
  }>;
  /** fileKey ของรูปสินค้า (รูปแรกตาม sortOrder) สำหรับแสดงใน order detail */
  imageFileKey?: string | null;
  /** จำนวนที่บันทึกรับคืน — รวม stock แล้ว */
  quantityReturnStocked?: number;
  /** จำนวนที่บันทึกรับคืน — รอ apply ตอนปิด returned */
  quantityReturnPending?: number;
}

export interface OrderStatusHistoryDTO {
  id: string;
  orderId: string;
  status: OrderStatus;
  note: string | null;
  createdAt: string;
}

export interface OrderShippingDTO {
  /** ค่าขนส่ง (หน่วยเล็กสุด); null = ยังไม่ได้คำนวณ */
  shippingFee: number | null;
  /** FK ແຂວງ (ສຳລັບແກ້ໄຂໃນແອັດມິນ) */
  shippingProvinceId: string | null;
  /** FK ເມືອງ */
  shippingDistrictId: string | null;
  /** ชื่อผู้รับ */
  shippingRecipientName: string | null;
  /** เบอร์โทรผู้รับ */
  shippingPhone: string | null;
  /** ບ້ານ / ທີ່ຢູ່ (snapshot) */
  shippingVillage: string | null;
  /** ชื่อจังหวัด (จาก province.name) */
  shippingProvinceName: string | null;
  /** ชื่ออำเภอ/แขวง (จาก district.name) */
  shippingDistrictName: string | null;
  /** โซนจัดส่ง (VTE / NEAR / FAR) */
  shippingZone: string | null;
  /** FK ຜູ້ໃຫ້ບໍລິການຂົນສົ່ງ (snapshot) */
  shippingCarrierId: string | null;
  /** ສາຂາ / ຈຸດຮັບ */
  carrierBranch: string | null;
  /** ຊື່ຜູ້ຂົນສົ່ງ (ຈາກ shipping_carrier.name) */
  shippingCarrierName: string | null;
}

export interface OrderHalShipmentDTO {
  /** Internal HAL shipment/pre-order ID returned in HAL ShipmentResource.id */
  preOrderId: number | null;
  /** HAL bill/tracking number. Keep synced to trackingNumber after HAL create succeeds. */
  shipmentNumber: string | null;
  shipmentStatusId: number | null;
  deliveryStateId: number | null;
  syncedAt: string | null;
  metadata: Record<string, unknown> | null;
}

export type OrderChannel = "web" | "retail" | "social";

export interface SocialSalesSourceRefDTO {
  id: string;
  code: string;
  name: string;
}

export interface OrderDTO {
  id: string;
  /** รหัสคำสั่งซื้อ เช่น ORD-0001, ORD-10000 */
  code: string;
  /** Storefront vs POS */
  channel: OrderChannel;
  customerId: string | null;
  status: OrderStatus;
  paymentMethod: "cod" | "online" | "cash" | "bank_transfer" | null;
  total: number;
  /** ส่วนลดท้ายบิล (หน่วยเล็กสุด) */
  discountTotal: number;
  /** ค่าขนส่ง (หน่วยเล็กสุด); null = ยังไม่ได้คำนวณ */
  shippingFee: number | null;
  shippingZone: string | null;
  trackingNumber: string | null;
  createdAt: string;
  updatedAt: string;
  /** ชื่อลูกค้าจาก user (เมื่อมี customerId) */
  customerName?: string | null;
  /** อีเมลลูกค้า */
  customerEmail?: string | null;
  /** เบอร์โทรลูกค้า (จาก user, มีใน detail) */
  customerPhone?: string | null;
  /** ที่อยู่จัดส่งเต็ม (มีเฉพาะใน detail) */
  shipping?: OrderShippingDTO | null;
  /** HAL Logistics mapping; null values mean no HAL shipment has been created yet. */
  halShipment?: OrderHalShipmentDTO | null;
  /** แหล่งขายโซเชียล (เมื่อ channel === social) */
  socialSalesSource?: SocialSalesSourceRefDTO | null;
}

/** Payment ที่ผูกกับ order (สำหรับแสดงใน order detail) */
export interface OrderPaymentDTO {
  id: string;
  orderId: string;
  amount: number;
  status:
    | "pending"
    | "paid"
    | "failed"
    | "partially_refunded"
    | "refunded"
    | "cancelled";
  paidAt: string | null;
  externalId: string | null;
  createdAt: string;
  /** ยอด refund ที่ succeeded สำหรับ payment นี้ (หน่วยเล็กสุด) */
  refundSucceededTotal?: number;
}

export interface OrderReturnLineDTO {
  id: string;
  orderId: string;
  orderItemId: string;
  quantityReturned: number;
  reason: string | null;
  stockAppliedAt: string | null;
  createdAt: string;
  createdBy: string | null;
}

export interface OrderDetailDTO extends OrderDTO {
  items: OrderItemDTO[];
  statusHistory: OrderStatusHistoryDTO[];
  /** รายการ payment ที่เกี่ยวข้องกับ order นี้ */
  payments: OrderPaymentDTO[];
  /** ยอดรวม refund ที่ succeeded ทุก payment ของ order (หน่วยเล็กสุด) */
  refundTotalSucceeded: number;
  /** รายการบรรทัดรับคืน (รายการย่อย) */
  returnLines: OrderReturnLineDTO[];
  /** ตัดสต็อกแล้วเมื่อยืนยันออเดอร์ (ISO timestamp) */
  stockDeductedAt: string | null;
  /** คืนสต็อกแล้วเมื่อยกเลิกจาก confirmed */
  stockRestoredAt: string | null;
  /** รูปแพ็ก / บิลขนส่ง */
  attachments: OrderAttachmentDTO[];
}

export interface OrderSummaryDTO {
  total: number;
  totalAmount: number;
  byStatus: Record<string, number>;
}

export const orderExportColumnKeySchema = z.enum([
  "orderCode",
  "lineItems",
  "quantity",
  "unitPrice",
  "lineAmount",
  "paymentCod",
  "paymentOnline",
  "shippingAddress",
  "phone",
  "customerName",
]);

export type OrderExportColumnKey = z.infer<typeof orderExportColumnKeySchema>;

export const OrderExportRequestSchema = z.object({
  limit: z.coerce.number().int().min(1).max(50_000).default(50_000),
  sort: z
    .array(z.object({ field: z.string(), dir: z.enum(["asc", "desc"]) }))
    .optional(),
  filters: z.array(z.any()).optional(),
  columns: z.array(orderExportColumnKeySchema).min(1),
});

export type OrderExportRequestInput = z.infer<typeof OrderExportRequestSchema>;

export interface OrderExportLineDTO {
  orderCode: string;
  lineDescription: string;
  quantity: number;
  unitPrice: number;
  /** ຍອດບັນທັດສິນຄ້າ (ຈາກ order_item.total) — ຄໍລຳ «ລາຄາ» ໃນ CSV */
  lineAmount: number;
  /** ສ່ວນເງິນທີ່ກຳນົດເປັນ COD ຕາມສັດສ່ວນບັນທັດ */
  paymentCod: number;
  /** ສ່ວນເງິນຊຳລະອອນໄລນ໌ / ໂອນ ຕາມສັດສ່ວນບັນທັດ */
  paymentOnline: number;
  shippingAddress: string;
  phone: string;
  customerName: string;
  isFirstLineOfOrder: boolean;
}

/** ລຳດັບຄໍລຳໃນ CSV */
export const ORDER_EXPORT_COLUMN_ORDER: OrderExportColumnKey[] = [
  "orderCode",
  "lineItems",
  "quantity",
  "unitPrice",
  "lineAmount",
  "paymentCod",
  "paymentOnline",
  "shippingAddress",
  "phone",
  "customerName",
];

export const ORDER_EXPORT_HEADER_LO: Record<OrderExportColumnKey, string> = {
  orderCode: "ເລກອໍເດີ້",
  lineItems: "ລາຍການສິນຄ້າ",
  quantity: "ຈຳນວນ",
  unitPrice: "ລາຄາຕໍ່ຫນ່ວຍ",
  lineAmount: "ລາຄາ",
  paymentCod: "COD",
  paymentOnline: "ໂອນແລ້ວ",
  shippingAddress: "ທີ່ຢູ່ຂົນສົ່ງ",
  phone: "ເບີໂທ",
  customerName: "ຊື່ລູກຄ້າ",
};

/** สถานะถัดไปที่อนุญาต (ຕາມ business flow) */
export function getNextAllowedStatuses(current: OrderStatus): OrderStatus[] {
  switch (current) {
    case "pending_payment":
      return ["confirmed", "cancelled"];
    case "confirmed":
      return ["packing", "cancelled"];
    case "packing":
      return ["shipped", "cancelled"];
    case "shipped":
      return ["completed", "cancelled", "return_in_transit"];
    case "return_in_transit":
      return ["returned"];
    case "completed":
      return ["return_in_transit"];
    case "returned":
    case "cancelled":
      return [];
    default:
      return [];
  }
}

/** Context ສຳລັບກວດ payment_slip (ອອນໄລນ໌ · ຊ່ອງ social ເທົ່ານັ້ນ) */
export type OrderAttachmentGateContext = {
  channel: OrderChannel | null | undefined;
  paymentMethod: OrderDTO["paymentMethod"];
};

/** อนุญาตให้เพิ่ม/ลบ attachment ตาม kind และสถานะออเดอร์ */
export function assertOrderAttachmentAllowedForStatus(
  kind: OrderAttachmentKind,
  status: OrderStatus,
  ctx?: OrderAttachmentGateContext,
): void {
  if (kind === "packing") {
    if (status !== "packing") {
      const e = new Error("ຮູບການແພັກອະນຸຍາດໃຫ້ເພີ່ມສະເພາະຕອນສະຖານະ «ກຳລັງແພັກ»");
      (e as { status?: number }).status = 400;
      throw e;
    }
    return;
  }
  if (kind === "shipping_document") {
    if (status !== "shipped" && status !== "completed") {
      const e = new Error("ຮູບບິນຂົນສົ່ງອະນຸຍາດໃຫ້ເພີ່ມສະເພາະຕອນ «ສົ່ງແລ້ວ» ຫຼື «ສຳເລັດ»");
      (e as { status?: number }).status = 400;
      throw e;
    }
    return;
  }
  if (kind === "payment_slip") {
    if (status !== "pending_payment") {
      const e = new Error("ສະລິບໂອນອະນຸຍາດສະເພາະຕອນ «ລໍຖ້າຊຳລະ»");
      (e as { status?: number }).status = 400;
      throw e;
    }
    if (ctx?.channel !== "social" || ctx?.paymentMethod !== "online") {
      const e = new Error("ສະລິບໂອນອະນຸຍາດສະເພາະອໍເດີ້Social Mediaທີ່ຊຳລະອອນໄລນ໌");
      (e as { status?: number }).status = 400;
      throw e;
    }
    return;
  }
}
